import * as React from "react";
import { useState, useEffect } from "react";
import {
  Filter,
  AutocompleteInput,
  Link,
  Toolbar,
  SaveButton,
  Button,
  TextInput,
  List,
  Create,
  Edit,
  LongTextInput,
  Datagrid,
  TabbedForm,
  EditButton,
  FormTab,
  SimpleForm,
  ReferenceInput,
  TextField,
  Pagination,
  ReferenceField,
  required,
  minLength,
  SelectInput
} from "react-admin";
import {
  values,
  map,
  prop,
  filter,
  isNil,
} from "ramda";
import GenerateMagicLink from "../components/generateMagicLink";
import styles from "../components/dashboard/Styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CambioEmail from "../components/CambioEmail";
import { getPermissionRole } from "../util";

const cardActionStyle = {
  zIndex: 2,
  //display: "inline-block",
  float: "right",
};

const status = [
  { id: 0, name: 'All' },
  { id: 1, name: 'Active' },
  { id: 2, name: 'Inactive'}
];

const AgencyPagination = (props) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

const AgencyFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Customer Id" source="id" alwaysOn />
    <TextInput label="Agency name" source="agency_name" alwaysOn />
    <TextInput label="Agency Email" source="email" alwaysOn />
    <SelectInput
      allowEmpty={false}
      label="Status"
      source="agency_status"
      choices={status} 
      optionText="name"
      optionValue="id"
      alwaysOn
    />
  </Filter>
);

const GenerateLink = (object) => {
  if ([1, 2, 3, 9].includes(getPermissionRole())) {
    return <GenerateMagicLink record={object.record} />;
  } else {
    return "";
  }
};

const validateForm = values => {
  const errors = {};
  if (
    values &&
    prop("password", values) !== prop("password_confirmation", values)
  ) {
    errors.password = ["Password and password confirmation mismatch"];
  }
  return errors;
};

export const AgencyList = (props) => (
  <List
    {...props}
    resource="agencies"
    pagination={<AgencyPagination />}
    filters={<AgencyFilter />}
    bulkActionButtons={false}
    filterDefaultValues={{ agency_status: 1 }}
  >
    <Datagrid>
      <TextField label="Agency ID" source="id" />
      <TextField label="Agency Name" source="agency_name" />
      <TextField label="City" source="agency_city" />
      <TextField label="State" source="agency_state" />
      <TextField label="Email" source="email" />
      <ReferenceField
        label="Country"
        source="city_id"
        reference="countries"
        linkType={false}
      >
        <TextField source="country" />
      </ReferenceField>
      <EditButton />
      <GenerateLink />
    </Datagrid>
  </List>
);

const AgencyTitle = (object) => (
  <span>
    {" Edit Agency "} &quot;
    {`${object.record.agency_name}`}
    &quot;
  </span>
);

const EditToolbar = (props) => {
  return (
    <Toolbar {...props}>
      {[1, 2, 3, 4, 6, 9].includes(getPermissionRole()) && (
        <SaveButton
          redirect={`/agencies`}
          style={{ padding: "10px", marginRight: 10 }}
        />
      )}
      {[1, 2, 3, 4, 6, 9].includes(getPermissionRole()) && (
        <CambioEmail
          basePath="agencies"
          style={{ backgroundColor: "red" }}
          label="Update Email"
        />
      )}
      <Button
        component={Link}
        to={{
          pathname: `/agencies`,
        }}
        label="Back to agencies"
      >
        <ArrowBackIcon />
      </Button>
    </Toolbar>
  );
};

const CreateToolbar = props => (
  <Toolbar {...props}>
    {[1, 2, 3, 4, 6, 9].includes(getPermissionRole()) && (
        <SaveButton
          redirect={`/agencies`}
          style={{ padding: "10px", marginRight: 10 }}
        />
      )}
    <Button
      component={Link}
      to={{
        pathname: `/agencies`
      }}
      label="Back to Agencies"
    >
      <ArrowBackIcon />
    </Button>
  </Toolbar>
);

export const AgencyEdit = ({ classes, ...props }) => (
  <Edit title={<AgencyTitle />} {...props}>
    <TabbedForm toolbar={<EditToolbar props={{ ...props }} />}>
      <FormTab label="General Data">
        <ReferenceField
          source="user_id"
          reference="users"
          label="Username"
          linkType={false}
        >
          <TextField source="username" />
        </ReferenceField>
        <ReferenceField
          source="user_id"
          reference="users"
          linkType={false}
          label="Email"
        >
          <TextField source="email" label="Email" />
        </ReferenceField>
        {/* <TextInput label="Other Email" source="other_email" /> */}
        <TextInput label="Agency Name" source="agency_name" />
        <TextInput label="CIF" source="cif" />
        <ReferenceInput label="Country" source="city_id" reference="countries">
          <AutocompleteInput optionText="country" />
        </ReferenceInput>
        <LongTextInput label="Address" source="agency_addres" />
        <TextInput label="City" source="agency_city" />
        <TextInput label="Zip" source="agency_zip" />
        <TextInput label="State" source="agency_state" />
        <TextInput label="Agency Phone" source="agency_phone" />
        <TextInput label="Agency Other Phone" source="agency_other_phone" />
      </FormTab>
    </TabbedForm>
  </Edit>
);

export const AgencyCreate = ({ classes, ...props }) => (
  <Create title="Create Agency" {...props}>
    <SimpleForm toolbar={<CreateToolbar props={{ ...props }} />} validate={validateForm}>
      <TextInput label="Agency Name" source="agency_name" validate={[required()]}/>
      <TextInput label="CIF" source="cif" />
      <ReferenceInput label="Country" source="city_id" reference="countries" 
                      perPage={999} sort={{ field: "id", order: "ASC" }}>
        <AutocompleteInput optionText="country" />
      </ReferenceInput>
      <TextInput label="City" source="agency_city" />
      <TextInput label="State" source="agency_state" />
      <TextInput label="Address" source="agency_addres" />
      <TextInput label="Zip" source="agency_zip" />
      <TextInput label="Phone" source="agency_phone" />
      <TextInput label="Other Phone" source="agency_other_phone" />
      <TextInput label="Occupation" source="agency_occupation" />
      <TextInput label="Email" source="email" type="email" validate={[required()]}/>
      <TextInput label="Password" source="password" type="password" validate={[required(), minLength(8)]}/>
      <TextInput label="Password Confirmation" source="password_confirmation" 
                 type="password" validate={[required(), minLength(8)]}
      />
    </SimpleForm>
  </Create>
);



// WEBPACK FOOTER //
// ./src/resources/agencies.js